import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, AlertTriangle, Check, Bookmark } from 'lucide-react'
import { getBookFile } from '../../../lib/readingFileStore'

// In-app PDF reader for on-device book files. pdf.js is dynamic-imported
// so it stays out of the main bundle (only loaded when a PDF is opened).
// The worker is served as a static asset from /pdf.worker.min.js (copied
// from pdfjs-dist at the pinned version) so rendering works offline.
//
// One page rendered to a canvas at a time, fit to container width and
// scaled by devicePixelRatio for crisp text. Progress is stored as
// percent-through-pages on reading_progress (shared with the text
// reader), so switching readers keeps a sensible resume point.
export function PdfReader({ book, savedPosition, progressLoaded, saveProgress, t }) {
  const navigate = useNavigate()
  const isRtl = book?.language === 'ar'

  const [status, setStatus] = useState('loading') // loading | missing | error | ready
  const [savedFlash, setSavedFlash] = useState(false)
  const [pageNum, setPageNum] = useState(1)
  const [numPages, setNumPages] = useState(0)

  const docRef = useRef(null)
  const canvasRef = useRef(null)
  const wrapRef = useRef(null)
  const renderTaskRef = useRef(null)
  const restoredRef = useRef(false)
  const saveTimerRef = useRef(null)

  // ---- Load the blob + pdf.js, open the document ----
  useEffect(() => {
    let alive = true
    setStatus('loading')
    ;(async () => {
      const rec = await getBookFile(book.id)
      if (!alive) return
      if (!rec?.blob) { setStatus('missing'); return }
      try {
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf')
        pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
        const data = await rec.blob.arrayBuffer()
        if (!alive) return
        const doc = await pdfjsLib.getDocument({ data }).promise
        if (!alive) { doc.destroy?.(); return }
        docRef.current = doc
        setNumPages(doc.numPages)
        setStatus('ready')
      } catch (e) {
        if (alive) setStatus('error')
      }
    })()
    return () => {
      alive = false
      try { renderTaskRef.current?.cancel?.() } catch { /* ignore */ }
      try { docRef.current?.destroy?.() } catch { /* ignore */ }
      docRef.current = null
    }
  }, [book.id])

  // ---- Restore saved page once the doc + progress are both ready ----
  useEffect(() => {
    if (restoredRef.current) return
    if (status !== 'ready' || !progressLoaded || numPages === 0) return
    restoredRef.current = true
    const target = Math.max(1, Math.min(numPages,
      Math.round(((savedPosition || 0) / 100) * Math.max(1, numPages - 1)) + 1))
    setPageNum(target)
  }, [status, progressLoaded, numPages, savedPosition])

  // ---- Render the current page whenever it (or the doc) changes ----
  const renderPage = useCallback(async () => {
    const doc = docRef.current
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!doc || !canvas || !wrap) return
    try { renderTaskRef.current?.cancel?.() } catch { /* ignore */ }
    const page = await doc.getPage(pageNum)
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const unscaled = page.getViewport({ scale: 1 })
    const cssWidth = wrap.clientWidth
    const scale = cssWidth / unscaled.width
    const viewport = page.getViewport({ scale: scale * dpr })
    const ctx = canvas.getContext('2d')
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    canvas.style.width = `${cssWidth}px`
    canvas.style.height = `${Math.floor(viewport.height / dpr)}px`
    const task = page.render({ canvasContext: ctx, viewport })
    renderTaskRef.current = task
    try { await task.promise } catch { /* cancelled */ }
  }, [pageNum])

  useEffect(() => {
    if (status !== 'ready') return undefined
    renderPage()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => renderPage()) : null
    if (ro && wrapRef.current) ro.observe(wrapRef.current)
    return () => ro?.disconnect()
  }, [status, renderPage])

  // ---- Persist progress (debounced) on page change ----
  useEffect(() => {
    if (status !== 'ready' || numPages === 0 || !restoredRef.current) return
    const pct = numPages > 1 ? ((pageNum - 1) / (numPages - 1)) * 100 : 0
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveProgress(pct), 600)
  }, [pageNum, numPages, status, saveProgress])

  const go = useCallback((delta) => {
    setPageNum((p) => Math.max(1, Math.min(numPages, p + delta)))
  }, [numPages])

  // Logical page turn: for RTL books the left edge advances.
  const turnPrev = () => go(isRtl ? 1 : -1)
  const turnNext = () => go(isRtl ? -1 : 1)

  // Horizontal swipe to turn pages (the canvas is in our DOM, so touch
  // events are available directly on the wrapper).
  const touchRef = useRef({ x: 0, y: 0, t: 0 })
  const onTouchStart = (e) => {
    const tt = e.changedTouches[0]
    touchRef.current = { x: tt.clientX, y: tt.clientY, t: Date.now() }
  }
  const onTouchEnd = (e) => {
    const tt = e.changedTouches[0]
    const { x, y, t: t0 } = touchRef.current
    const dx = tt.clientX - x, dy = tt.clientY - y
    if (Date.now() - t0 < 700 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      if (dx < 0) turnNext(); else turnPrev()
    }
  }

  const onSaveSpot = useCallback(() => {
    const pct = numPages > 1 ? ((pageNum - 1) / (numPages - 1)) * 100 : 0
    saveProgress(pct)
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 1500)
  }, [numPages, pageNum, saveProgress])

  const header = (
    <header className="flex items-center justify-between border-b border-neutral-200 bg-card px-4 py-2 dark:border-neutral-800" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
      <button
        type="button"
        onClick={() => navigate(`/reading/${book.id}`)}
        className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
        aria-label={t('reading.back')}
      >
        <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
      </button>
      <div className="min-w-0 flex-1 px-2 text-center">
        <p dir="auto" className="line-clamp-1 text-sm font-medium">{book.title}</p>
      </div>
      <button
        type="button"
        onClick={onSaveSpot}
        className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
        aria-label={t('reading.saveSpot')}
      >
        {savedFlash ? <Check className="h-5 w-5 text-emerald-500" /> : <Bookmark className="h-5 w-5" />}
      </button>
    </header>
  )

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-neutral-100 dark:bg-neutral-950">
      {header}

      <div ref={wrapRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="relative flex-1 overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {status === 'loading' && (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin opacity-60" /></div>
        )}
        {status === 'missing' && (
          <div className="mx-auto max-w-xs p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 h-7 w-7 text-amber-500" />
            <p dir="auto" className="text-sm opacity-80">{t('reading.file.missing')}</p>
            <button type="button" onClick={() => navigate(`/reading/${book.id}`)} className="mt-4 rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white">
              {t('reading.back')}
            </button>
          </div>
        )}
        {status === 'error' && (
          <p dir="auto" className="px-6 pt-10 text-center text-sm text-rose-500">{t('reading.readerLoadFailed')}</p>
        )}
        {status === 'ready' && (
          <>
            <div className="h-full overflow-y-auto overflow-x-hidden py-2">
              <canvas ref={canvasRef} className="mx-auto block" />
            </div>
            <button type="button" onClick={turnPrev} aria-label={t('reading.previousPage')} className="absolute inset-y-0 start-0 w-[28%]" />
            <button type="button" onClick={turnNext} aria-label={t('reading.nextPage')} className="absolute inset-y-0 end-0 w-[28%]" />
            {numPages > 1 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
                <span dir="ltr" className="rounded-full bg-black/55 px-2.5 py-0.5 text-[10px] font-medium text-white">
                  {pageNum} / {numPages}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default PdfReader
